import memoizeOne from 'memoize-one';
import React, { Component, ReactNode } from 'react';
import { Product, ReferencePrice } from '~/Api/User/ProductGroup/RoMaxPrice';
import { DebounceInput } from '~/Debounce';
import parseHumanFloat from '~/utils/Number/parseHumanFloat';
import { calculatePrice } from '~/RoMaxPrice/calculatePrice';
import { Link } from '~/UI/Button';
import Checkbox from '~/UI/Checkbox/Checkbox';
import Icon from '~/UI/Icon/Icon';
import inputCompactStyles from '~/UI/Input/compact.less';
import Input from '~/UI/Input/Input';
import defaultSegmentedSelectStyles from '~/UI/SegmentedSelect/default.less';
import SegmentedSelectOption from '~/UI/SegmentedSelect/Option/SegmentedSelectOption';
import SegmentedSelectPage from '~/UI/SegmentedSelect/Page/SegmentedSelectPage';
import SegmentedSelectSegment from '~/UI/SegmentedSelect/Segment/SegmentedSelectSegment';
import SegmentedSelect from '~/UI/SegmentedSelect/SegmentedSelect';
import { ProductStatusIcon } from '~/libs/ui/atoms/ProductStatusIcon';

import styles from './styles.less';
import { isInvalidProduct } from '~/Product/isAlternativeProduct';
import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip/TailwindTooltip';
import { CountrySourceWithTooltip } from '~/libs/ui/molecules/CountrySourceWithTooltip';

export interface ReferencePriceValue {
	parentValue: null;
	prompt: ReactNode;
	value: string;
}
export interface Values {
	packageSize: {
		isValid: boolean | null;
		value: string;
	};
	packageSizeForSingleUnit: {
		isValid: boolean | null;
		value: string;
	};
	referencePrice: {
		isValid: boolean;
		value: ReferencePriceValue | null;
	};
	selected: boolean;
}
export type OnChangeListener = (
	product: Product,
	referencePrice: ReferencePriceValue | null,
	packageSize: string,
	packageSizeForSingleUnit: string
) => void;

export type OnSelectListener = (product: Product, selected: boolean, range: boolean) => void;

interface Props {
	detailLink(product: { id: string }): { pathname: string; search: string };
	disableSelection: boolean;
	highlightProduct: boolean;
	roMaxPriceProduct: Product;
	usedForCalculation: boolean;
	lastSelectedProduct: boolean;
	onChange: OnChangeListener;
	onSelect: OnSelectListener;
	referencePackageSize: number | null;
	style: React.CSSProperties;
	values: Values;
	onSave(): void;
	checked: boolean;
	checkRow(value: boolean): void;
}

export default class Row extends Component<Props> {
	public static defaultProps = {
		disableSelection: false,
		highlightProduct: null,
		lastSelectedProduct: null,
		referencePackageSize: null,
		style: null,
	};

	private readonly resolveReferencePrice = memoizeOne(
		(type: string | null, roMaxPriceProduct: Product): ReferencePrice | null =>
			type !== null
				? roMaxPriceProduct.referencePrices.find((referencePrice) => referencePrice.calculatedPrice.type === type) ??
				  null
				: null
	);

	shouldComponentUpdate(nextProps: Readonly<Props>) {
		return (
			this.props.disableSelection !== nextProps.disableSelection ||
			this.props.highlightProduct !== nextProps.highlightProduct ||
			JSON.stringify(this.props.roMaxPriceProduct) !== JSON.stringify(nextProps.roMaxPriceProduct) ||
			this.props.usedForCalculation !== nextProps.usedForCalculation ||
			this.props.lastSelectedProduct !== nextProps.lastSelectedProduct ||
			this.props.onChange !== nextProps.onChange ||
			this.props.onSelect !== nextProps.onSelect ||
			this.props.referencePackageSize !== nextProps.referencePackageSize ||
			this.props.style !== nextProps.style ||
			this.props.values.packageSize.isValid !== nextProps.values.packageSize.isValid ||
			this.props.values.packageSize.value !== nextProps.values.packageSize.value ||
			this.props.values.packageSizeForSingleUnit.isValid !== nextProps.values.packageSizeForSingleUnit.isValid ||
			this.props.values.packageSizeForSingleUnit.value !== nextProps.values.packageSizeForSingleUnit.value ||
			this.props.values.referencePrice.isValid !== nextProps.values.referencePrice.isValid ||
			this.props.values.referencePrice.value?.value !== nextProps.values.referencePrice.value?.value ||
			this.props.values.selected !== nextProps.values.selected ||
			this.props.checked !== nextProps.checked
		);
	}

	public render() {
		const {
			roMaxPriceProduct,
			usedForCalculation,
			values,
			referencePackageSize,
			disableSelection,
			highlightProduct,
			lastSelectedProduct,
			detailLink,
			style,
			checked,
			onChange,
			onSave,
			checkRow,
		} = this.props;
		const { product } = roMaxPriceProduct;
		const detailLinkString = detailLink(product);
		const referencePrice = this.resolveReferencePrice(values.referencePrice.value?.value || null, roMaxPriceProduct);
		const showReferencePricesSelect = roMaxPriceProduct.referencePrices.length > 1;
		const prompt = referencePrice !== null ? this.renderReferencePricePrompt(referencePrice) : null;

		return (
			<div
				className={
					isInvalidProduct(roMaxPriceProduct)
						? styles.removedRow
						: highlightProduct
						? styles.highlightedRow
						: styles.row
				}
				data-testid='row'
				style={style}
			>
				<div className={styles.checkboxContainer} data-testid='delete-product'>
					<Checkbox checked={checked} onChange={checkRow} />
				</div>
				<div className={styles.countryCell}>
					<Link styles={{ button: styles.countryCellContent }} tabIndex={-1} to={detailLinkString}>
						<CountrySourceWithTooltip
							countryAlpha={product.dataset.source.country.alpha2}
							countryName={product.dataset.source.country.name}
							dataSourceShortName={product.dataset.source.shortName}
							dataSourceDescription={product.dataset.source.description}
						/>
					</Link>
				</div>
				<div className={styles.brandCell}>
					<Link styles={{ button: styles.brandCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedBrandName}
					</Link>
				</div>
				<div className={styles.mahCell}>
					<Link styles={{ button: styles.mahCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedMarketingAuthorizationHolder}
					</Link>
				</div>
				<div className={styles.dosageCell}>
					<Link styles={{ button: styles.dosageCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedDosageForm}
					</Link>
				</div>
				<div className={styles.dosageStrengthCell}>
					<Link styles={{ button: styles.dosageStrengthCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.dosageStrength}
					</Link>
				</div>
				<div className={styles.packageCell}>
					{roMaxPriceProduct.singleUnitPackageSize ? (
						<div className={styles.packageInput}>
							<DebounceInput
								value={values.packageSizeForSingleUnit.value}
								onBlur={onSave}
								onChange={(value) => {
									onChange(roMaxPriceProduct, values.referencePrice.value, values.packageSize.value, value);
								}}
							>
								<Input
									id={`product-package-${product.id}`}
									isInvalid={values.packageSizeForSingleUnit.isValid === false}
									styles={inputCompactStyles}
								/>
							</DebounceInput>
						</div>
					) : (
						<Link styles={{ button: styles.packageCellContent }} tabIndex={-1} to={detailLinkString}>
							{product.packageSize}
						</Link>
					)}
				</div>
				<div className={styles.referencePriceCell}>
					{showReferencePricesSelect ? (
						this.renderReferencePriceSelect(prompt)
					) : (
						<div className={styles.priceCellContent}>{prompt}</div>
					)}
					{roMaxPriceProduct.singleUnitPackageSize && (
						<TailwindTooltip
							reference={<Icon name='red-exclamation-mark'>!</Icon>}
							displayClass='!absolute top-4 right-0 inline-block h-4 align-top'
							position='left'
						>
							Single unit package size
						</TailwindTooltip>
					)}
				</div>
				<div className={styles.packageForCalculationCell}>
					<div className={styles.packageInput}>
						<DebounceInput
							value={values.packageSize.value}
							onBlur={onSave}
							onChange={(value) => {
								onChange(roMaxPriceProduct, values.referencePrice.value, value, values.packageSizeForSingleUnit.value);
							}}
						>
							<Input
								id={`product-package-for-calc-${product.id}`}
								isInvalid={values.packageSize.isValid === false}
								placeholder={String(referencePackageSize || '')}
								styles={inputCompactStyles}
							/>
						</DebounceInput>
					</div>
				</div>
				<div className={styles.comparativePriceCell}>
					<Link
						data-testid='calculated-price'
						styles={{ button: styles.comparativePriceCellContent }}
						tabIndex={-1}
						to={detailLinkString}
					>
						{this.renderCalculatedPrice(referencePrice)}
					</Link>
				</div>
				<div className={styles.selectionCell}>
					<ProductStatusIcon status={roMaxPriceProduct.groupItemStatus} />
					<Checkbox
						disabled={disableSelection}
						focused={lastSelectedProduct}
						checked={!disableSelection && values.selected}
						id={`product-selection-${product.id}`}
						onChange={this.selectionChanged}
					/>
				</div>
				<div className={styles.usedForCalculationCell}>
					<Link styles={{ button: styles.usedForCalculationCellContent }} tabIndex={-1} to={detailLinkString}>
						{usedForCalculation ? <Icon name='formula' title='Used for calculation' /> : null}
					</Link>
				</div>
			</div>
		);
	}

	private renderCalculatedPrice(referencePrice: ReferencePrice | null) {
		if (referencePrice === null) {
			return null;
		}

		const {
			values: { packageSize },
			referencePackageSize,
		} = this.props;
		if (
			packageSize.isValid === false ||
			(packageSize.isValid === null && packageSize.value !== '') ||
			referencePackageSize === null
		) {
			return null;
		}

		const parsedPackageSize = packageSize.value !== '' ? parseHumanFloat(packageSize.value) : referencePackageSize;
		if (parsedPackageSize === null) {
			return null;
		}
		return calculatePrice(
			referencePrice.calculatedPrice.price,
			parsedPackageSize,
			referencePackageSize
		).roundedHumanFormat();
	}

	private renderReferencePricePrompt(price: ReferencePrice) {
		return (
			<abbr data-testid='cell-reference-price' title={price.calculatedPrice.description}>
				<div>{price.calculatedPrice.price.roundedHumanFormat()}</div>
				<div>
					<strong>{price.sourcePrice.price?.roundedHumanFormat()}</strong>
				</div>
			</abbr>
		);
	}

	private renderReferencePriceSelect(prompt: ReactNode) {
		const { roMaxPriceProduct, values, onChange } = this.props;

		return (
			<SegmentedSelect
				isValid={values.referencePrice.isValid}
				prompt={prompt}
				selectedOption={values.referencePrice.value}
				styles={defaultSegmentedSelectStyles}
				onSelect={(value) => {
					onChange(roMaxPriceProduct, value, values.packageSize.value, values.packageSizeForSingleUnit.value);
				}}
			>
				<SegmentedSelectPage>
					<SegmentedSelectSegment>
						{roMaxPriceProduct.referencePrices.map((price) => (
							<SegmentedSelectOption
								key={price.calculatedPrice.type}
								prompt={this.renderReferencePricePrompt(price)}
								value={price.calculatedPrice.type}
							>
								<div>{price.calculatedPrice.description}</div>
								<div>{price.calculatedPrice.price.roundedHumanFormat()} Comparative price</div>
								<div>
									<strong>
										{price.sourcePrice.price?.roundedHumanFormat()} {price.sourcePrice.humanFormatType()}
									</strong>
								</div>
							</SegmentedSelectOption>
						))}
					</SegmentedSelectSegment>
				</SegmentedSelectPage>
			</SegmentedSelect>
		);
	}

	private readonly selectionChanged = (checked: boolean, event: React.MouseEvent<HTMLLabelElement, MouseEvent>) => {
		const { roMaxPriceProduct, onSelect } = this.props;
		onSelect(roMaxPriceProduct, checked, event.nativeEvent.shiftKey);
	};
}
